import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import SharedData from '../../common/SharedData'
import Loader from '../../common/Loader'
import Paginator from '../../common/Paginator'
import {getStockRequests, markStockRequestsSeen} from '../core/_requests'
import {IStockRequest, STOCK_REQUEST_STATUSES, isStockRequestStatus} from '../core/_models'
import {directionClasses} from '../core/direction'
import {extractErrorMessage} from '../core/errors'
import {notifyStockRequestsSeen} from '../core/useUnseenStockRequests'
import StockRequestStatus from './StockRequestStatus'
import StockRequestDetailsModal from './StockRequestDetailsModal'

const PAGE_SIZE = 20

type Props = {
  refreshKey: number
}

const StockRequestHistory: FC<Props> = ({refreshKey}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const dir = directionClasses(language)
  const token = currentUser?.token
  const [searchParams, setSearchParams] = useSearchParams()
  const [requests, setRequests] = useState<IStockRequest[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewId, setViewId] = useState<number | null>(null)
  const seenSent = useRef(false)
  const requestSeq = useRef(0)

  const page = Math.max(1, parseInt(searchParams.get('current_page') ?? '1', 10) || 1)
  const statusParam = searchParams.get('status')
  const status = isStockRequestStatus(statusParam) ? statusParam : ''

  const fetchRequests = useCallback(async () => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await getStockRequests({page, limit: PAGE_SIZE, status}, token, language)
      if (seq !== requestSeq.current) return
      setRequests(res.data?.data ?? [])
      setTotal(res.data?.total ?? 0)
      if (!seenSent.current) {
        seenSent.current = true
        markStockRequestsSeen(token, language)
          .then(() => notifyStockRequestsSeen())
          .catch(() => {})
      }
    } catch (err) {
      if (seq !== requestSeq.current) return
      setRequests([])
      setTotal(0)
      setError(extractErrorMessage(err) || formatMessage({id: 'add_to_stock.load_requests_error'}))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [page, status, token, language, formatMessage])

  useEffect(() => {
    void fetchRequests()
  }, [fetchRequests, refreshKey])

  const changeStatus = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('status', value)
    else next.delete('status')
    next.delete('current_page')
    setSearchParams(next)
  }

  const renderBody = () => {
    if (loading) return <Loader />
    if (error) return <div className='text-danger text-center py-10'>{error}</div>
    if (requests.length === 0) {
      return (
        <div className='text-muted text-center py-10'>
          {formatMessage({id: 'add_to_stock.no_requests'})}
        </div>
      )
    }
    return (
      <>
        <div className='table-responsive'>
          <table className='table table-row-bordered table-row-gray-200 align-middle gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className={`${dir.padStart} ${dir.roundStart} ${dir.start}`}>
                  {formatMessage({id: 'add_to_stock.request_number'})}
                </th>
                <th className={`${dir.start}`}>{formatMessage({id: 'date_added'})}</th>
                <th className='text-center'>{formatMessage({id: 'add_to_stock.total_items'})}</th>
                <th className='text-center'>
                  {formatMessage({id: 'add_to_stock.total_quantity'})}
                </th>
                <th className={`${dir.start}`}>{formatMessage({id: 'status'})}</th>
                <th className={`${dir.end} ${dir.padEnd} ${dir.roundEnd}`}>
                  {formatMessage({id: 'actions'})}
                </th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr
                  key={request.request_id}
                  className={request.is_new ? 'bg-light-primary' : undefined}
                  data-testid={`stock-request-${request.request_id}`}
                >
                  <td className={`${dir.padStart} ${dir.start} fw-bold text-nowrap`}>
                    #{request.request_id}
                    {request.is_new && (
                      <span className={`badge badge-light-primary ${dir.marginStart}`}>
                        {formatMessage({id: 'add_to_stock.new_badge'})}
                      </span>
                    )}
                  </td>
                  <td className={`${dir.start} text-nowrap`}>{request.date_added}</td>
                  <td className='text-center'>{request.total_items}</td>
                  <td className='text-center'>{request.total_quantity}</td>
                  <td className={`${dir.start}`}>
                    <StockRequestStatus request={request} />
                  </td>
                  <td className={`${dir.end} ${dir.padEnd}`}>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary'
                      onClick={() => setViewId(request.request_id)}
                    >
                      {formatMessage({id: 'view'})}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > PAGE_SIZE && (
          <div className='d-flex justify-content-end mt-4'>
            <Paginator currentPage={page} totalResult={total} perPage={PAGE_SIZE} />
          </div>
        )}
      </>
    )
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            {formatMessage({id: 'add_to_stock.history.title'})}
          </span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            {formatMessage({id: 'add_to_stock.history.subtitle'})}
          </span>
        </h3>
        <div className='card-toolbar'>
          <select
            className='form-select form-select-sm form-select-solid w-200px'
            aria-label={formatMessage({id: 'status'})}
            value={status}
            onChange={(e) => changeStatus(e.target.value)}
          >
            <option value=''>{formatMessage({id: 'all'})}</option>
            {STOCK_REQUEST_STATUSES.map((code) => (
              <option key={code} value={code}>
                {formatMessage({id: `add_to_stock.status.${code}`})}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='card-body py-4'>{renderBody()}</div>
      <StockRequestDetailsModal requestId={viewId} onClose={() => setViewId(null)} />
    </div>
  )
}

export default StockRequestHistory
